import { HttpClient } from "@utils/httpClient";
import type {
  StartBatchResponse,
  BatchJobStatusResponse,
  TextUnit,
} from "@models/batch";
import type { ListTranslationsResponse } from "@models/translation";

const baseUrl = (import.meta.env.VITE_TRANSLATION_API_BASE_URL ??
  import.meta.env.VITE_API_BASE_URL ?? "") as string;

const client = new HttpClient(baseUrl);

export interface StartBatchApiRequest {
  source_lang: string;
  target_lang: string[];
  fileExtension: string;
  batch_size: number;
  batch_size_prompt: number;
  llm_mode: string;
  llm_model: string;
  enable_cache: boolean;
  /** Original uploaded file name, stored in history so it survives a reload. */
  original_file_name?: string;
}

export const batchApi = {
  /**
   * Step 1: Start a batch translation job.
   * Returns a job_id and a presigned uploadUrl.
   */
  startBatch(body: StartBatchApiRequest): Promise<StartBatchResponse> {
    return client.makeAuthenticatedRequest<StartBatchResponse>(
      "/v1/startBatchTranslation",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  /**
   * Step 2: Upload text units to the presigned URL returned by startBatch.
   * This is a plain PUT (no auth header needed — it's a presigned S3 URL).
   *
   * The units are mapped to the backend's documented input contract
   * (`[{ id, text }]`). The backend's `_normalize_texts` keys entries by `id`;
   * sending our internal `text_id` field would be ignored and the entry id
   * would silently fall back to the array index, so downloaded result ids would
   * no longer match the caller's original ids. Mapping here preserves them.
   */
  async uploadTextUnits(presignedUrl: string, textUnits: TextUnit[]): Promise<void> {
    const payload = textUnits.map((u) => ({ id: u.text_id, text: u.text }));
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Failed to upload text units: ${response.status}`);
    }
  },

  /**
   * Step 3: Poll the status of a batch job.
   */
  getJobStatus(jobId: string): Promise<BatchJobStatusResponse> {
    const params = new URLSearchParams({ translation_id: jobId });
    return client.makeAuthenticatedRequest<BatchJobStatusResponse>(
      `/v1/getBatchTranslation?${params.toString()}`,
      { method: "GET" },
    );
  },

  /** Fetch the authenticated user's batch job history (newest first). */
  listBatchHistory(limit = 50): Promise<ListTranslationsResponse> {
    return client.makeAuthenticatedRequest<ListTranslationsResponse>(
      `/v1/translations?type=batch&limit=${limit}`,
      { method: "GET" },
    );
  },

  /** Delete a single batch history entry by its content hash (sk). */
  deleteBatchHistoryEntry(contentHash: string): Promise<{ status: string; deleted: number }> {
    return client.makeAuthenticatedRequest<{ status: string; deleted: number }>(
      `/v1/translations/${encodeURIComponent(contentHash)}`,
      { method: "DELETE" },
    );
  },

  /** Regenerate fresh presigned download URLs for a completed batch job. */
  getBatchDownloadUrls(jobId: string): Promise<{
    id: string;
    global_status: string;
    presigned_urls?: Record<string, string> | null;
  }> {
    return client.makeAuthenticatedRequest(
      `/v1/batchTranslations/${encodeURIComponent(jobId)}/download`,
      { method: "GET" },
    );
  },
};
